const formatCategories = (categories) => (categories && Object.values(categories)
  .map((category, i) => ({
    media: {
      src: category?.media?.src || '',
      alt: category.media?.alt,
    },
    link: {
      href: category.target,
      title: category.text,
    },
    authoredIndex: category.index,
    key: `${category.text}-${i}`,
    style: category?.style,
    customClass: category.media?.customClass,
  }))) || [];

const formatBrandRail = (categories) => (categories && Object.values(categories)
  .map((category) => ({
    media: {
      src: category?.src,
    },
    link: {
      href: category.target,
      title: category.text,
    },
    key: category.key,
  }))) || [];

export const formatPaletteConfig = (contentObj = {}) => {
  if (!contentObj) return null;
  const { config: { palette } = {} } = contentObj;

  return { content: palette } || null;
};

export const formatBannerComponent = (contentObj = {}, defaults = {}) => {
  if (!contentObj) return null;

  // Apply values from CMS to all breakpoints
  const formatCvsValues = (val) => {
    if (!val) return null;
    const map = val.split(', ');

    const mappedVals = {
      xs: map[0],
      'xs-plus': map[1],
      sm: map[2],
      md: map[3],
      lg: map[4],
    };
    return {
      xs: mappedVals.xs,
      'xs-plus': mappedVals['xs-plus'] || mappedVals.xs,
      sm: mappedVals.sm || mappedVals['xs-plus'] || mappedVals.xs,
      md: mappedVals.md || mappedVals.sm || mappedVals['xs-plus'] || mappedVals.xs,
      lg: mappedVals.lg || mappedVals.md || mappedVals.sm || mappedVals['xs-plus'] || mappedVals.xs,
    };
  };

  // Apply fallbacks to defaults (so that we dont have to explicity set each size)
  const formatDefaultIntoBreakpoints = (obj) => {
    if (!obj) return null;
    return {
      xs: obj.xs,
      'xs-plus': obj['xs-plus'] || obj.xs,
      sm: obj.sm || obj['xs-plus'] || obj.xs,
      md: obj.md || obj.sm || obj['xs-plus'] || obj.xs,
      lg: obj.lg || obj.lg || obj.md || obj.sm || obj['xs-plus'] || obj.xs,
    };
  };

  const {
    config: {
      'content-position': contentPosition,
      'banner-position': bannerPosition,
      'banner-height': bannerHeight,
      'banner-gradient': bannerGradient,
    } = {},
  } = contentObj;

  return {
    contentPosition: formatCvsValues(contentPosition)
      || formatDefaultIntoBreakpoints(defaults.contentPosition),
    bannerPosition: formatCvsValues(bannerPosition)
     || formatDefaultIntoBreakpoints(defaults.bannerPosition),
    bannerHeight: (bannerHeight || defaults.bannerHeight),
    bannerGradient: formatCvsValues(bannerGradient)
    || formatDefaultIntoBreakpoints(defaults.bannerGradient),
  };
};

/**
 * formatMediaConfig
 * @description Creates a object with the correct format use by
 * @param media {Object} model with media properties
 * @param options {Object} additional options to add to config object
 * @param isLockUp {Boolean} If the config need to pull data from the lockup
 *    the image component
 * @exports {Object} Ready to user media object
 */
const formatMediaConfig = (media, options = {}, isLockUp = false, customKeyModifier = '') => {
  // if (!media?.xs && !media?.image) return null;
  const keyModifier = (customKeyModifier && `${customKeyModifier}-`)
    || (isLockUp && 'lockup-')
    || '';
  const image = `${keyModifier}image`;
  const xs = `${keyModifier}xs`;
  const sm = `${keyModifier}sm`;
  const md = `${keyModifier}md`;
  const lg = `${keyModifier}lg`;
  const mp4 = `${keyModifier}mp4`;
  const webm = `${keyModifier}webm`;
  const poster = `${keyModifier}poster`;
  return {
    xs: media?.[xs]?.src || media?.[image]?.src,
    sm: media?.[sm]?.src,
    md: media?.[md]?.src,
    lg: media?.[lg]?.src,
    mp4: media?.[mp4]?.src,
    webm: media?.[webm]?.src,
    poster: media?.[poster]?.src,
    alt: media?.[xs]?.alt ?? media?.[image]?.alt,
    ...options,
  };
};

/**
 * formatEyebrow
 * @description Pulls the eyebrow text out of content objects
 * @param contentObj {Object} content model nested under componentModels object.
 * @returns {*|string} Eyebrow text
 */
const formatEyebrow = (contentObj) => contentObj?.text?.eyebrow?.text || '';

/**
 * formatCta
 * @description Pulls the cta object out of content objects
 * @param contentObj {Object} content model nested under componentModels object.
 * @returns {*|Object} cta object
 */
const formatCta = (contentObj, customKeyModifier) => {
  const key = customKeyModifier || 'cta';
  const action = contentObj?.actions?.[key];
  if (action) {
    return {
      ...action,
      href: action.target,
      modifier: action.style || 'primary',
    };
  }
  return {};
};

/**
 * formatHeadings
 * @description Pulls the heading out of content objects
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {String} Heading text
 */
const formatHeading = (contentObj) => contentObj?.text?.heading?.text || '';

/**
 * formatSubheadings
 * @description Pulls the subheading out of content objects
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {String} Subheading text
 */
const formatSubheading = (contentObj) => contentObj?.text?.subheading?.text || '';

/**
 * formatCustomText
 * @description Pulls a custom text out of content objects
 * @param contentObj {Object} content model object nested under componentModels object.
 * @param key {String} the key to use while retrieving text
 * @exports {String} text
 */
const formatCustomText = (key, contentObj) => contentObj?.text?.[key]?.text || '';

/**
 * formatBody
 * @description Pulls the body text out of content objects
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {String} Body text
 */
const formatBody = (contentObj) => contentObj?.text?.body?.text || '';

/**
 * formatStickyNav
 * @description Formats props for StickyNav component
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {Object} Props
 */
const formatStickyNav = (contentObj, optStickyNav) => {
  const { actions } = contentObj || {};
  const { categoryName } = optStickyNav || {};
  return (actions && Object.values(actions)
    .map((action) => {
      let ariaLabel = '';
      if (categoryName) {
        ariaLabel = `${action.text} ${action.text.toLowerCase() === 'learn' ? 'about ' : ''}${categoryName}`;
      }
      return {
        anchor: action.target,
        text: action.text,
        ariaLabel: ariaLabel ?? action.text,
      };
    })) || [];
};

export const formatContentPositionConfig = (contentObj = {}) => {
  const {
    config: {
      'content-position': contentPosition,
    } = {},
  } = contentObj;

  return contentPosition || undefined;
};

export const formatLandingBadge = (contentObj = {}, options = {}) => {
  const {
    config: {
      'badge-text': text,
      'badge-theme': theme,
    } = {},
  } = contentObj;
  const {
    accentColor,
  } = options;

  return {
    text,
    theme,
    accentColor,
  };
};

/**
 * formatActions
 * @description Formats multiple actions and divides them into javascript vs default actions (links)
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {Object} Props
 * default: { defaultActions: [], javascriptActions: [] }
 */
const formatActions = (contentObj, actionOptions = {}) => Object.values(contentObj?.actions || {})
  .reduce((acc, action) => {
    let analyticsId = '';
    let ariaLabel = '';
    const {
      useCustomAnalyticId,
      useCustomText,
      horizontalSection,
      heading,
      subheading,
    } = actionOptions || {};
    const {
      'all-secondary-buttons': allSecondaryButtons,
    } = contentObj?.config || {};

    if (useCustomAnalyticId) {
      analyticsId = `${heading || subheading || ''}_${horizontalSection}_${action?.text}`;
    }

    if (useCustomText) {
      ariaLabel = `${action.text} about ${heading || subheading || ''}`;
    }

    if (allSecondaryButtons === 'true') {
      acc.allSecondaryButtons = true;
    }

    if (action.target === 'javascript') {
      acc.javascriptActions.push({
        ...action,
        newTab: false,
        href: '',
        target: 'javascript',
        tag: 'button',
        modifier: action.style || 'primary',
        analyticsId,
        ariaLabel,
      });
    } else if (action.name === 'alternate-link') {
      acc.alternateLink = {
        ...action,
        href: action.target,
        tag: 'a',
        newTab: false,
        analyticsId,
        ariaLabel,
      };
    } else {
      acc.defaultActions.push({
        ...action,
        newTab: action.customClass?.includes('new-tab'),
        modifier: action.style || 'primary',
        noFollow: action.customClass?.includes('nofollow'),
        analyticsId,
        ariaLabel,
      });
    }
    return acc;
  }, {
    defaultActions: [], javascriptActions: [], alternateLink: {}, allSecondaryButtons: false,
  });

/**
 * formatContentComponent
 * @description Formats props for @rei/content-block.
 * @param contentObj {Object} content model object nested under componentModels object.
 * @param config {Object} config options for component.
 * @param headingOptions {Object} config options for heading.
 * @exports {Object} Props
 */
const formatContentComponent = (contentObj, config, headingOptions, actionsOptions = {}) => {
  if (!contentObj) return null;
  const lockup = formatMediaConfig(contentObj?.media, {}, true);
  const eyebrow = formatEyebrow(contentObj);
  let heading = formatHeading(contentObj);
  const subheading = formatSubheading(contentObj);
  const body = formatBody(contentObj);
  const actions = formatActions(contentObj, { ...actionsOptions, heading, subheading });
  const palette = formatPaletteConfig(contentObj);

  // If theres no heading, use the lockup alt text for heading and make it SR only.
  const useLockupAsHeading = !heading;
  if (useLockupAsHeading) {
    heading = lockup.alt;
    lockup.alt = '';
  }

  if (
    lockup.xs
    || lockup.src
    || eyebrow
    || heading
    || subheading
    || body
    || actions?.defaultActions?.length
    || actions?.javascriptActions?.length
    || (actions?.alternateLink?.href && actions?.alternateLink?.text)
  ) {
    return {
      lockup,
      eyebrow,
      heading: {
        text: heading,
        srOnly: useLockupAsHeading,
        ...headingOptions,
      },
      subheading,
      body,
      actions,
      config: { palette, ...config },
    };
  }
  return null;
};

const formatOfferCards = (contentObj) => {
  if (!contentObj) return null;
  const { children } = contentObj;
  const cards = children && Object.values(children).map((card, i) => {
    const category = formatCustomText('category', card);
    const categoryContext = formatCustomText('category-context', card);
    const headingSmall = formatCustomText('heading-small', card);
    const headingLarge = formatCustomText('heading-large', card);
    const amountOff = formatCustomText('amount-off', card);
    const couponCode = formatCustomText('coupon-code', card);
    const heading = formatHeading(card);
    const badge = formatLandingBadge(card);
    const { modelName: cardModelName } = card;
    const body = formatBody(card);
    const href = card?.actions?.primary?.target || '';
    const media = formatMediaConfig(card?.media);
    const index = i + 1;
    return {
      category,
      categoryContext,
      headingSmall,
      headingLarge,
      amountOff,
      couponCode,
      heading,
      modelName: cardModelName,
      body,
      badge,
      href,
      media,
      index,
    };
  });
  return cards;
};

/**
 * formatMiniBanner
 * @description Formats props for MiniBanner component
 * @param contentObj {Object} content model object nested under componentModels object.
 * @exports {Object} Props
 */
const formatMiniBannerComponent = (contentObj, defaults = {}, contentBlockHeadingOpts = {}) => {
  if (!contentObj) return null;
  const contentBlockOpts = {
    contentAlign: {
      xs: 'center',
    },
  };
  const media = formatMediaConfig(contentObj?.media);
  const content = formatContentComponent(contentObj, contentBlockOpts, contentBlockHeadingOpts);
  const background = contentObj?.config?.background || defaults.background;
  const { padding } = defaults;

  return (((media.xs || content || background || padding) && {
    background,
    padding,
    media,
    content,
  }) || null);
};

const formatStory = (
  story,
  headingOptsChild,
  contentConfig,
  actionsOptions
) => ({
  media: formatMediaConfig(story?.media),
  content: formatContentComponent(
    story,
    contentConfig,
    headingOptsChild,
    actionsOptions
  ),
  config: story?.config,
});

const formatLandingStoryComponent = (
  contentObj,
  options,
  headingOpts,
  headingOptsChild,
  contentConfig,
  actionsOptions
) => {
  const { children = {} } = contentObj || {};
  const {
    aspectRatio, grid, storyStackPosition, background,
  } = options || {};
  const stories = Object.values(children)
    ?.map(
      (child, index) => formatStory(
        child,
        headingOptsChild,
        contentConfig,
        { ...actionsOptions, horizontalSection: index + 1 }
      )
    ) || null;
  return (
    (stories.length > 0 && {
      headingSection: {
        ...headingOpts,
        text: formatHeading(contentObj),
      },
      stories,
      aspectRatio,
      grid,
      storyStackPosition,
      background,
    })
    || null
  );
};

const formatLandingFeaturedTilesComponent = (contentObj, config) => {
  const { children = {} } = contentObj || {};
  const featuredTiles = Object.values(children).map((featuredTile) => ({
    action: featuredTile.actions?.primary
      ? formatCta(featuredTile, 'primary')
      : formatCta(featuredTile),
    body: formatBody(featuredTile),
    media: formatMediaConfig(featuredTile.media),
  }));
  return (
    (!!featuredTiles.length && {
      featuredTiles,
      config,
    })
    || null
  );
};

export {
  formatContentComponent,
  formatCategories,
  formatBrandRail,
  formatMediaConfig,
  formatEyebrow,
  formatCta,
  formatActions,
  formatCustomText,
  formatHeading,
  formatSubheading,
  formatBody,
  formatStickyNav,
  formatOfferCards,
  formatMiniBannerComponent,
  formatLandingStoryComponent,
  formatStory,
  formatLandingFeaturedTilesComponent,
};
